import React, { useState } from "react";
import { useNavigate } from 'react-router-dom';
import { addUserIfNotExists, checkCouponCode } from "../../dbApiFunctions";
import { isEmail } from "validator";
import "./loginStyle.css";
import { copyBaseUserDataTemplate } from "../../config/aws";
import { hashString } from "../../utils/utilities";
import { showSuccessAlert, showErrorAlert, showWarningAlert, showInfoAlert, showConfirmDialog } from '../../utils/sweetAlertConfig';
import '../../styles/sweetAlert.css';
import { getAuth, createUserWithEmailAndPassword, sendEmailVerification, signInWithEmailAndPassword } from "firebase/auth";
import PhoneInput from 'react-phone-input-2';
import 'react-phone-input-2/lib/style.css';

const Register = ({ setIsLoggedIn }) => {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    username: "",
    email: "",
    password: "",
    confirmPassword: "",
    couponCode: "",
    phone: ""
  });
  const [errors, setErrors] = useState({});
  const [successful, setSuccessful] = useState(false);
  const [message, setMessage] = useState("");
  const [emailVerificationSent, setEmailVerificationSent] = useState(false);
  const [isSendingEmailVerification, setIsSendingEmailVerification] = useState(false);
  const [isRegistering, setIsRegistering] = useState(false);

  const validateForm = () => {
    const newErrors = {};

    if (!formData.username) {
      newErrors.username = "Username is required";
    } else if (formData.username.length < 3 || formData.username.length > 20) {
      newErrors.username = "Username must be between 3 and 20 characters";
    }

    if (!formData.email) {
      newErrors.email = "Email is required";
    } else if (!isEmail(formData.email)) {
      newErrors.email = "Please enter a valid email";
    } else if (formData.email.toLowerCase().endsWith("@gmail.com") || formData.email.toLowerCase().endsWith("@yahoo.com") || formData.email.toLowerCase().endsWith("@hotmail.com")) {
      newErrors.email = "Only business emails are allowed";
    }

    if (!formData.password) {
      newErrors.password = "Password is required";
    } else if (formData.password.length < 6 || formData.password.length > 40) {
      newErrors.password = "Password must be between 6 and 40 characters";
    }

    if (!formData.confirmPassword) {
      newErrors.confirmPassword = "Please confirm your password";
    } else if (formData.confirmPassword !== formData.password) {
      newErrors.confirmPassword = "Passwords do not match";
    }

    if (!formData.phone) {
      newErrors.phone = "Mobile number is required";
    } else if (formData.phone.replace(/\D/g, '').length < 8 || formData.phone.replace(/\D/g, '').length > 15) {
      newErrors.phone = "Please enter a valid phone number (8-15 digits)";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleRegistration = async (e) => {
    e.preventDefault();
    setMessage("");
    setSuccessful(false);

    if (!validateForm()) {
      return;
    }

    setIsRegistering(true);

    let creditsToUse = 100;

    // Validate coupon code if provided
    if (formData.couponCode) {
      try {
        const couponResponse = await checkCouponCode(formData.couponCode);
        if (!couponResponse.status) {
          showErrorAlert(
            'Invalid Coupon',
            couponResponse.message,
            'Please check your coupon code and try again'
          );
          setIsRegistering(false);
          return;
        }
        creditsToUse = creditsToUse + couponResponse.credits;
      } catch (error) {
        console.error(error);
        showErrorAlert(
          'Coupon Validation Error',
          'Error validating coupon code',
          'Please try again or contact support'
        );
        setIsRegistering(false);
        return;
      }
    }

    try {
      // Create Firebase authentication account
      const auth = getAuth();
      const userCredential = await createUserWithEmailAndPassword(
        auth,
        formData.email,
        formData.password
      );

      // Send email verification
      setIsSendingEmailVerification(true);
      try {
        await sendEmailVerification(userCredential.user);
        setEmailVerificationSent(true);
        
        showSuccessAlert(
          'Email Verification Sent!',
          'Please check your email and click the verification link.',
          'You must verify your email before completing registration.'
        );

        // Store user data temporarily (don't complete registration yet)
        const hashedEmail = await hashString(formData.email);
        sessionStorage.setItem("temp_username", hashedEmail);
        sessionStorage.setItem("temp_email", formData.email);
        sessionStorage.setItem("temp_phone", formData.phone);
        sessionStorage.setItem("temp_password", formData.password);
        sessionStorage.setItem("temp_credits", creditsToUse.toString());
        sessionStorage.setItem("temp_username_value", formData.username);  
        
        const response = await addUserIfNotExists(
          formData.username,
          formData.phone,
          formData.email,
          formData.password,
          creditsToUse
        );

        if (response.status) {
          await copyBaseUserDataTemplate(hashedEmail);
        } 

        // Show verification reminder
        showInfoAlert(
          'Email Verification Required',
          'Please check your inbox and click the verification link.',
          'You can resend the verification email if needed.'
        );

      } catch (verificationError) {
        console.error("Email verification error:", verificationError);
        
        // Clean up Firebase auth user if verification fails
        try {
          await userCredential.user.delete();
        } catch (deleteError) {
          console.error("Error cleaning up Firebase auth user:", deleteError);
        }

        showErrorAlert(
          'Email Verification Failed',
          'Failed to send verification email.',
          'Please try again or contact support.'
        );
      }

    } catch (firebaseError) {
      console.error("Firebase registration error:", firebaseError);
      
      // Handle specific Firebase error codes
      let errorMessage = "Registration failed. Please try again.";
      let errorTitle = "Registration Error";
      let errorDetails = "An unexpected error occurred during registration.";

      switch (firebaseError.code) {
        case 'auth/email-already-in-use':
          try {
            const auth = getAuth();
            // Try to sign in with the provided credentials
            const existingUserCredential = await signInWithEmailAndPassword(auth, formData.email, formData.password);
            if (!existingUserCredential.user.emailVerified) {
              // Resend verification email
              await sendEmailVerification(existingUserCredential.user);
              errorMessage = "Email Not Verified";
              errorTitle = "Verification Email Resent";
              errorDetails = "A verification email has been resent to your address. Please check your inbox and click the verification link to activate your account.";
              setEmailVerificationSent(true);              
            }
          } catch (signInError) {
            // If sign-in fails, likely due to wrong password
            errorMessage = "An account with this email already exists.";
            errorTitle = "Email Already Exists (Reset Password from Login Page)";
            errorDetails = "Please use a different email or try logging in instead.";  
          }
          break;
        case 'auth/invalid-email':
          errorMessage = "Please enter a valid email address.";
          errorTitle = "Invalid Email";
          errorDetails = "The email format is not recognized.";
          break;
        case 'auth/operation-not-allowed':
          errorMessage = "Email/password accounts are not enabled.";
          errorTitle = "Registration Disabled";
          errorDetails = "Please contact support for assistance.";
          break;
        case 'auth/weak-password':
          errorMessage = "Password is too weak.";
          errorTitle = "Weak Password";
          errorDetails = "Please choose a stronger password (at least 6 characters).";
          break;
        case 'auth/network-request-failed':
          errorMessage = "Network error. Please check your connection.";
          errorTitle = "Network Error";
          errorDetails = "Please ensure you have a stable internet connection.";
          break;
        default:
          errorMessage = "Registration failed. Please try again.";
          errorTitle = "Registration Error";
          errorDetails = "An unexpected error occurred. Please contact support if the problem persists.";
      }
      
      showErrorAlert(
        errorTitle,
        errorMessage,
        errorDetails
      );      
    } finally {
      setIsRegistering(false);
      setIsSendingEmailVerification(false);
    }
  };

  const handleResendEmailVerification = async () => {
    setIsSendingEmailVerification(true);
    
    try {
      const auth = getAuth();
      const currentUser = auth.currentUser;
      
      if (currentUser) {
        await sendEmailVerification(currentUser);
        showSuccessAlert(
          'Verification Email Resent!',
          'Please check your inbox for the verification link.',
          'Make sure to check your spam folder as well.'
        );
      } else {
        showErrorAlert(
          'User Not Found',
          'Please try registering again.',
          'The user session has expired.'
        );
      }
    } catch (error) {
      console.error("Error resending verification email:", error);
      showErrorAlert(
        'Failed to Resend',
        'Error sending verification email.',
        'Please try again or contact support.'
      );
    } finally {
      setIsSendingEmailVerification(false);
    }
  };

  const handleEmailVerificationCheck = async () => {
    try {
      const auth = getAuth();
      await auth.currentUser?.reload();
      
      if (auth.currentUser?.emailVerified) {
        // Email is verified, complete registration
        const tempCredits = parseInt(sessionStorage.getItem("temp_credits") || "100");
        const tempUsername = sessionStorage.getItem("temp_username_value");
        const tempPhone = sessionStorage.getItem("temp_phone");
        const tempEmail = sessionStorage.getItem("temp_email");
        const tempPassword = sessionStorage.getItem("temp_password");
        
        // Create Firestore user document
        const response = await addUserIfNotExists(
          tempUsername,
          tempPhone,
          tempEmail,
          tempPassword,
          tempCredits
        );

        if (response.status) {
          const hashedEmail = await hashString(tempEmail);
          await copyBaseUserDataTemplate(hashedEmail);
          
          // Set session storage
          sessionStorage.setItem("username", hashedEmail);
          sessionStorage.setItem("email", tempEmail);
          sessionStorage.setItem("password", tempPassword);
          
          // Clean up temporary data
          sessionStorage.removeItem("temp_username");
          sessionStorage.removeItem("temp_email");
          sessionStorage.removeItem("temp_phone");
          sessionStorage.removeItem("temp_password");
          sessionStorage.removeItem("temp_credits");
          sessionStorage.removeItem("temp_username_value");
          
          setIsLoggedIn(true);
          
          showSuccessAlert(
            'Registration Complete!',
            'Your email has been verified and account created successfully.',
            'Setting up your account... You will be redirected shortly.'
          );

          // Reload after a short delay
          setTimeout(() => {
            window.location.reload();
          }, 1000);
        } else {
          showErrorAlert(
            'Registration Failed',
            response.message || 'Failed to create user profile',
            'Please try again or contact support'
          );
        }
      } else {
        showWarningAlert(
          'Email Not Verified',
          'Please check your email and click the verification link.',
          'If you haven\'t received the email, try resending it.'
        );
      }
    } catch (error) {
      console.error("Error checking email verification:", error);
      showErrorAlert(
        'Verification Check Failed',
        'Error checking email verification status.',
        'Please try again or contact support.'
      );
    }
  };

  return (
    <div className="col-md-12 login-container">
      <div className="card card-container light-theme">
        <h3 className="login-title">Create Account</h3>

        <form onSubmit={handleRegistration} className="auth-form">
          {!successful && (
            <div>
              {!emailVerificationSent ? (
                <>
                  <div className="form-group">
                    <label htmlFor="username">Username</label>
                    <input
                      type="text"
                      className={`form-control light-input ${errors.username ? 'is-invalid' : ''}`}
                      name="username"
                      value={formData.username}
                      onChange={handleInputChange}
                      placeholder="Choose a username"
                    />
                    {errors.username && <div className="invalid-feedback">{errors.username}</div>}
                  </div>

                  <div className="form-group">
                    <label htmlFor="email">Email Address</label>
                    <input
                      type="email"
                      className={`form-control light-input ${errors.email ? 'is-invalid' : ''}`}
                      name="email"
                      value={formData.email}
                      onChange={handleInputChange}
                      placeholder="Enter your email address"
                    />
                    {errors.email && <div className="invalid-feedback">{errors.email}</div>}
                  </div>

                  <div className="form-group">
                    <label htmlFor="phone">Mobile Number</label>
                    <PhoneInput
                      country={'us'}
                      value={formData.phone}
                      onChange={phone => setFormData(prev => ({ ...prev, phone }))}
                      inputProps={{
                        name: 'phone',
                        required: true,
                        className: `form-control light-input ${errors.phone ? 'is-invalid' : ''}`,
                        autoFocus: false,
                        placeholder: 'e.g. +1 555 123 4567'
                      }}
                      inputStyle={{ width: '100%', height: '48px', fontSize: '1rem', boxSizing: 'border-box' }}
                      containerStyle={{ width: '100%', marginBottom: '15px' }}
                      buttonStyle={{ borderRadius: '6px 0 0 6px', height: '48px' }}
                      dropdownStyle={{ borderRadius: '0 0 6px 6px' }}
                    />
                    {errors.phone && <div className="invalid-feedback" style={{ display: 'block' }}>{errors.phone}</div>}
                  </div>

                  <div className="form-group">
                    <label htmlFor="password">Password</label>
                    <input
                      type="password"
                      className={`form-control light-input ${errors.password ? 'is-invalid' : ''}`}
                      name="password"
                      value={formData.password}
                      onChange={handleInputChange}
                      placeholder="Create a password"
                    />
                    {errors.password && <div className="invalid-feedback">{errors.password}</div>}
                  </div>

                  <div className="form-group">
                    <label htmlFor="password">Confirm Password</label>
                    <input
                      type="password"
                      className={`form-control light-input ${errors.confirmPassword ? 'is-invalid' : ''}`}
                      name="confirmPassword"
                      value={formData.confirmPassword}
                      onChange={handleInputChange}
                      placeholder="Confirm password"
                    />
                    {errors.confirmPassword && <div className="invalid-feedback">{errors.confirmPassword}</div>}
                  </div>

                  <div className="form-group">
                    <label htmlFor="couponCode">Coupon Code <span className="optional-label">(optional)</span></label>
                    <input
                      type="text"
                      className="form-control light-input"
                      name="couponCode"
                      value={formData.couponCode}
                      onChange={handleInputChange}
                      placeholder="Enter coupon code if available"
                    />
                  </div>

                  <div className="form-group">
                    <button
                      type="submit"
                      className="btn gradient-btn"
                      disabled={isRegistering}
                    >
                      {isRegistering ? (
                        <>
                          <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                          Creating Account...
                        </>
                      ) : (
                        "Create Account"
                      )}
                    </button>
                  </div>
                </>
              ) : (
                <>
                  <div className="email-verification-section">
                    <div className="alert alert-info" role="alert">
                      <h5 className="alert-heading">
                        <i className="fas fa-envelope me-2"></i>
                        Email Verification Required
                      </h5>
                      <p>
                        We've sent a verification email to <strong>{formData.email}</strong>. 
                        Please check your inbox and click the verification link to complete your registration.
                      </p>
                      <hr />
                      <p className="mb-0">
                        <small>
                          <i className="fas fa-info-circle me-1"></i>
                          Don't forget to check your spam folder if you don't see the email.
                        </small>
                      </p>
                    </div>

                    <div className="form-group">
                      <button
                        type="button"
                        className="btn btn-outline-primary me-2"
                        onClick={handleEmailVerificationCheck}
                        disabled={isSendingEmailVerification}
                      >
                        {isSendingEmailVerification ? (
                          <>
                            <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                            Checking...
                          </>
                        ) : (
                          <>
                            <i className="fas fa-check me-2"></i>
                            I've Verified My Email
                          </>
                        )}
                      </button>

                      <button
                        type="button"
                        className="btn btn-outline-secondary"
                        onClick={handleResendEmailVerification}
                        disabled={isSendingEmailVerification}
                      >
                        {isSendingEmailVerification ? (
                          <>
                            <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                            Sending...
                          </>
                        ) : (
                          <>
                            <i className="fas fa-redo me-2"></i>
                            Resend Verification Email
                          </>
                        )}
                      </button>
                    </div>

                    <div className="form-group mt-3">
                      <button
                        type="button"
                        className="btn btn-link"
                        onClick={() => {
                          setEmailVerificationSent(false);
                          setFormData({
                            username: "",
                            email: "",
                            password: "",
                            confirmPassword: "",
                            couponCode: "",
                            phone: ""
                          });
                        }}
                      >
                        <i className="fas fa-arrow-left me-2"></i>
                        Back to Registration
                      </button>
                    </div>
                  </div>
                </>
              )}
            </div>
          )}
        </form>
      </div>
    </div>
  );
};

export default Register;